import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {
    approveRentalRequest,
    getRequestRentalWithRentalAsset,
    rejectRentalRequest,
    returnRentalRequest
} from "../../../api/lifecycle/rental/rentalRequestService";
import {PageHeader} from "../../../components/PageHeader";
import {
    ContainerTitle,
    InfoContainer,
    InfoContainerTitle,
    InfoItem,
    InfoTitle,
    RentalInfo,
    SubContainer
} from "../rentalAsset/rentalAssetViewCss";
import RentalHistory from "../rentalHistory/RentalHistory";
import {handleActiveToast} from "../../../components/sonner/ToastBasic";
import {changeRefreshRentalRequest} from "../../../redux/slice/statusSlice";
import {RentalType} from "../../../constant/CodeSpecNum";
import {calculateLeftDateRate, calculateRemainingDays, handleSetUserInfo} from "../../../components/Utils/SimmsUtils";
import {ProgressBarBasic} from "../../../components/progressBar/ProgressBar";

const RentalRequestUserView = () => {
    const [rentalRequest, setRentalRequest] = useState({});

    const refresh = useSelector(state => state.status.refreshRentalRequest);
    const {t} = useTranslation();
    const {id} = useParams();

    useEffect(() => {
        if (!id) return;
        setRentalRequest({});
        handleCallDate();
    }, [id, refresh]);

    const handleCallDate = async () => {
        if (id) {
            await getRequestRentalWithRentalAsset(id, (data) => {
                setRentalRequest({...data});
            });
        }
    }

    return (
        <div className="common-container border-l-gray" style={{height: "calc(100vh - 30rem)", display: "flex", flexDirection: "column"}}>
            <PageHeader header={"대여신청"} indicator={"라이프사이클 > 대여신청 > 상세"}/>
            <div className={'flex border border-gray-300 p-2 gap-6 justify-between shrink-0'}>
                <SubContainer className={'bg-[#f5f5f5] w-[550px]'}>
                    <ContainerTitle>대여자산 정보</ContainerTitle>
                    <InfoContainer>
                        <RentalInfo>
                            <InfoTitle>자산명</InfoTitle>
                            <InfoItem>{rentalRequest.assetName}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>자산코드</InfoTitle>
                            <InfoItem>{rentalRequest.assetCode}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여그룹</InfoTitle>
                            <InfoItem>{rentalRequest.codeName}</InfoItem>
                        </RentalInfo>
                    </InfoContainer>
                </SubContainer>
                <SubContainer className={'flex-1 h-full'}>
                    <ContainerTitle>대여 정보</ContainerTitle>
                    <RentalAssetInfo rentalRequest={rentalRequest}/>
                </SubContainer>
            </div>
            <div className={'border border-gray-300 px-3 pt-2 pb-1 mt-1'} style={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                <RentalHistory rentalInfo={{id: rentalRequest.id}} showRentalRequestHistory/>
            </div>
        </div>
    );
};

export default RentalRequestUserView;

const RentalAssetInfo = ({rentalRequest}) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const handleReturn = async () => {
        if (window.confirm(t(`confirmReturnRental`))) {
            const data = {
                rentalAssetId: rentalRequest.rentalAssetId,
                rentalRequestId: rentalRequest.id,
            };
            await returnRentalRequest(data, () => {
                handleActiveToast({message: t(`complete`)});
                dispatch(changeRefreshRentalRequest());
            });
        }
    }

    const handleApprove = async () => {
        if (window.confirm(t(`confirmRent`))) {
            const data = {
                rentalAssetId: rentalRequest.rentalAssetId,
                rentalRequestId: rentalRequest.id,
            };
            await approveRentalRequest(data, () => {
                handleActiveToast({message: t(`complete`)});
                dispatch(changeRefreshRentalRequest());
            });
        }
    }

    const handleReject = async () => {
        if (window.confirm(t(`confirmRejectRental`))) {
            const data = {
                rentalAssetId: rentalRequest.rentalAssetId,
                rentalRequestId: rentalRequest.id,
                rentalType: RentalType.REJECT.value,
            }
            await rejectRentalRequest(data, () => {
                handleActiveToast({message: t(`complete`)});
                dispatch(changeRefreshRentalRequest());
            });
        }
    }

    return (
        <>
            <div className={"flex gap-[2rem] mx-8 justify-center"}>
                <InfoContainer className={"w-[22rem]"}>
                    <InfoContainerTitle>신청 정보</InfoContainerTitle>
                    <div className={"mt-3"}>
                        <RentalInfo>
                            <InfoTitle>대여 신청일</InfoTitle>
                            <InfoItem>{rentalRequest?.createdAt}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여 신청자</InfoTitle>
                            <InfoItem>{handleSetUserInfo(rentalRequest.rentUser)}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여 상태</InfoTitle>
                            <InfoItem>{rentalRequest.rentalTypeName}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여 기간</InfoTitle>
                            {!rentalRequest.extendDate ?
                                <div>
                                    <div className={"flex p-[4px]"}>
                                        <div className={'w-[10rem]'}>{rentalRequest.startDate} ~ {rentalRequest.endDate}</div>
                                        {rentalRequest.rentalType === RentalType.RENT.value &&
                                            <div className={'w-[5rem]'}>{calculateRemainingDays(rentalRequest.endDate)}</div>
                                        }
                                    </div>
                                    {rentalRequest.rentalType === RentalType.RENT.value &&
                                        <ProgressBarBasic
                                            done={calculateLeftDateRate(rentalRequest.startDate, rentalRequest.endDate)}
                                            total={100}
                                        />
                                    }
                                </div>
                                :
                                <div>
                                    <div className={"flex gap-[2rem] p-[4px]"}>
                                        <div>{rentalRequest.startDate} ~ {rentalRequest.extendDate}</div>
                                        {rentalRequest.rentalType === RentalType.RENT.value &&
                                            <div>{calculateRemainingDays(rentalRequest.extendDate)}</div>
                                        }
                                    </div>
                                    {rentalRequest.rentalType === RentalType.RENT.value &&
                                        <ProgressBarBasic
                                            done={calculateLeftDateRate(rentalRequest.startDate, rentalRequest.extendDate)}
                                            total={100}
                                        />
                                    }
                                </div>
                            }
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여 연장일</InfoTitle>
                            <InfoItem>{rentalRequest.extendDate ? `${rentalRequest.extendDate} (기존 반납일: ${rentalRequest.endDate}}` : ""}</InfoItem>
                        </RentalInfo>
                    </div>
                </InfoContainer>
                <InfoContainer className={"w-[20rem]"}>
                    <InfoContainerTitle>대여 담당자 정보</InfoContainerTitle>
                    <div className={"mt-3"}>
                        <RentalInfo>
                            <InfoTitle>대여 실행일</InfoTitle>
                            <InfoItem>{rentalRequest?.rentApproveDate}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>대여 승인자</InfoTitle>
                            <InfoItem>{handleSetUserInfo(rentalRequest.rentApprover)}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>기간연장 실행일</InfoTitle>
                            <InfoItem>{rentalRequest?.extendApproveDate}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>기간연장 승인자</InfoTitle>
                            <InfoItem>{handleSetUserInfo(rentalRequest.extendApprover)}</InfoItem>
                        </RentalInfo>
                    </div>
                </InfoContainer>
                <InfoContainer className={"w-[20rem]"}>
                    <InfoContainerTitle>반납 담당자 정보</InfoContainerTitle>
                    <div className={"mt-3"}>
                        <RentalInfo>
                            <InfoTitle>반납 승인일</InfoTitle>
                            <InfoItem>{rentalRequest?.returnDate}</InfoItem>
                        </RentalInfo>
                        <RentalInfo>
                            <InfoTitle>반납 승인자</InfoTitle>
                            <InfoItem>{handleSetUserInfo(rentalRequest.returnApprover)}</InfoItem>
                        </RentalInfo>
                    </div>
                </InfoContainer>
            </div>
        </>
    );
}
